import { Http, HttpRequestConfig, HttpInstance, HttpCancelToken } from '@okapi/http';
import { ErrorWithStatus } from './error-with-status';

export type ApiRequestConfig = HttpRequestConfig;
export type CancelToken = HttpCancelToken;

class Api {
  http: HttpInstance;

  constructor() {
    this.http = new Http({
      headers: {
        Accept: 'application/json, text/plain, */*',
      },
    });
  }

  public abortController = () => this.http.abortController();

  post = <Response = unknown, Data extends object = Record<string, unknown>>(
    url: string,
    data: Data,
    config?: ApiRequestConfig,
  ) =>
    this._doFetch<Response>(url, {
      method: 'POST',
      data,
      headers: {
        ...config?.headers,
        Accept: 'application/json',
        'Content-Type': 'application/json;charset=UTF-8',
      },
      ...config,
    });

  put = <Response = unknown, Data extends object = Record<string, unknown>>(
    url: string,
    data: Data,
    config?: ApiRequestConfig,
  ) =>
    this._doFetch<Response>(url, {
      method: 'PUT',
      data,
      headers: {
        ...config?.headers,
        Accept: 'application/json',
        'Content-Type': 'application/json;charset=UTF-8',
      },
      ...config,
    });

  get = <Response = unknown, Data extends Record<string, string> = Record<string, string>>(
    url: string,
    data: Data,
    config?: ApiRequestConfig,
  ) => {
    const params = new URLSearchParams(data).toString();
    const resultUrl = `${url}?${params}`;

    return this._doFetch<Response>(resultUrl, { method: 'GET', ...config });
  };

  delete = <Response = unknown, Data extends Record<string, string> = Record<string, string>>(
    url: string,
    data: Data,
    config?: ApiRequestConfig,
  ) => {
    const params = new URLSearchParams(data).toString();
    const resultUrl = `${url}?${params}`;

    return this._doFetch<Response>(resultUrl, {
      method: 'DELETE',
      ...config,
    });
  };

  private _doFetch<Response = unknown>(url: string, init: ApiRequestConfig) {
    const resultUrl = /^\/\//.test(url as string) ? `https:${url}` : url;

    return this.http
      .request<Response>(resultUrl, init)
      .then((response) =>
        response.status >= 200 && response.status < 300 ? response.data : Promise.reject(response),
      )
      .catch((e) => {
        if (__IS_SERVER__) {
          let data = e.response?.data;

          try {
            // we want to prettify data object. But if it's not an object(maybe a cut of HTML code) this statement will fail.
            // that's why we should wrap `JSON.stringify` with `try {}` block
            // If this statement will fail, we will output `data` field as-is
            data = JSON.stringify(data, null, 2);
            // eslint-disable-next-line no-empty
          } catch {}
          // eslint-disable-next-line no-console
          console.log({
            status: e.response?.status,
            statusText: e.response?.statusText,
            requestID: init?.headers?.['x-request-id'],
            date: e.response?.headers?.date,
            url: e.response?.config?.url,
            data,
            message: e.data?.message,
            stack: e.stack,
          });
        }

        if (e.status >= 400) {
          throw new ErrorWithStatus(e.data.message, e.status);
        }

        return e.response;
      });
  }
}

export const api = new Api();
